import { useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { CandlestickChart, Cpu, Plug, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";

export default function AdminTrading() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [durInput, setDurInput] = useState("");
  const [assets, setAssets] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/settings/trading");
      setCfg(data);
      setDurInput((data.available_durations || []).join(", "));
    } catch { toast.error("Failed to load trading settings"); }
    try {
      const { data } = await api.get("/market/assets");
      setAssets(data || []);
    } catch { /* non-blocking */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upd = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const updExt = (patch) => setCfg((c) => ({ ...c, external: { ...c.external, ...patch } }));
  const setPayout = (sym, val) => setCfg((c) => {
    const payouts = { ...(c.payouts || {}) };
    if (val === "" || val === null) delete payouts[sym];
    else payouts[sym] = Number(val);
    return { ...c, payouts };
  });

  const save = async () => {
    setBusy(true);
    try {
      const durations = durInput.split(",").map((x) => parseInt(x.trim(), 10)).filter((n) => n > 0);
      const payload = {
        live_trading_enabled: cfg.live_trading_enabled,
        engine: cfg.engine,
        internal_live_enabled: cfg.internal_live_enabled,
        min_trade_amount: Number(cfg.min_trade_amount),
        max_trade_amount: Number(cfg.max_trade_amount),
        default_duration: Number(cfg.default_duration),
        available_durations: durations,
        trading_fee_pct: Number(cfg.trading_fee_pct),
        default_payout_pct: Number(cfg.default_payout_pct),
        payouts: Object.fromEntries(Object.entries(cfg.payouts || {}).map(([k, v]) => [k, Number(v)])),
        external: {
          provider: cfg.external.provider || "",
          enabled: cfg.external.enabled,
          sandbox: cfg.external.sandbox,
          api_key: cfg.external._new_key || "",       // blank keeps existing
          api_secret: cfg.external._new_secret || "",
        },
      };
      const { data } = await api.put("/admin/settings/trading", payload);
      setCfg(data);
      setDurInput((data.available_durations || []).join(", "));
      toast.success("Trading settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save"); }
    finally { setBusy(false); }
  };

  const testConn = async () => {
    try {
      const { data } = await api.post("/admin/trading/test-connection");
      (data.ok ? toast.success : toast.info)(data.message);
    } catch { toast.error("Test failed"); }
  };

  if (!cfg) return <div className="text-fx-s3">Loading trading settings…</div>;

  const activeExternal = cfg.active_engine === "external";

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-trading">
      <div className="flex items-center gap-2">
        <CandlestickChart className="h-5 w-5 text-brandblue" />
        <div>
          <h3 className="font-display text-xl font-bold">Live Trading Configuration</h3>
          <p className="text-xs text-fx-s3">Control how Live Trading operates across the platform. All trades are validated and settled server-side.</p>
        </div>
      </div>

      {/* Active engine banner */}
      <div data-testid="active-engine-banner" className={`rounded-xl border p-4 flex items-center gap-3 ${activeExternal ? "border-gold/40 bg-gold/10" : "border-pos/30 bg-pos/10"}`}>
        {activeExternal ? <Plug className="h-5 w-5 text-gold" /> : <Cpu className="h-5 w-5 text-pos" />}
        <div>
          <div className="text-sm font-semibold">{activeExternal ? "EXTERNAL LIVE TRADING" : "INTERNAL LIVE TRADING"}</div>
          <div className="text-xs text-fx-s3">
            {cfg.live_trading_enabled
              ? (activeExternal
                ? "Live trades route to the configured external exchange."
                : "Live trades run on the internal engine (server-side entry/exit, P/L and settlement).")
              : "Live Trading is currently DISABLED for all users."}
          </div>
        </div>
      </div>

      {/* Core toggles */}
      <div className="rounded-xl bg-fx-s1 border border-white/10 p-4 space-y-4">
        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <div><div className="text-sm font-medium">Enable Live Trading</div><div className="text-xs text-fx-s3">Master switch. When off, users see a clear "not yet enabled" message.</div></div>
          <Switch data-testid="live-enabled-switch" checked={cfg.live_trading_enabled} onCheckedChange={(v) => upd({ live_trading_enabled: v })} />
        </label>
        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <div><div className="text-sm font-medium">Internal Live Trading</div><div className="text-xs text-fx-s3">Use the platform's built-in engine (no external exchange needed).</div></div>
          <Switch data-testid="internal-live-switch" checked={cfg.internal_live_enabled} onCheckedChange={(v) => upd({ internal_live_enabled: v })} />
        </label>
        <div className="space-y-1">
          <Label className="text-xs">Trading engine</Label>
          <Select value={cfg.engine} onValueChange={(v) => upd({ engine: v })}>
            <SelectTrigger data-testid="engine-select" className="bg-fx-base border-white/10"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-fx-s2 border-white/10 text-white">
              <SelectItem value="internal">Internal engine</SelectItem>
              <SelectItem value="external">External exchange</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[11px] text-fx-s3">If "external" is selected but not configured/connected, the system automatically falls back to the internal engine.</p>
        </div>
      </div>

      {/* Trade rules */}
      <div className="rounded-xl bg-fx-s1 border border-white/10 p-4 space-y-3">
        <div className="text-sm font-semibold">Trade rules</div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Min trade amount ($)</Label><Input data-testid="min-amount-input" type="number" value={cfg.min_trade_amount} onChange={(e) => upd({ min_trade_amount: e.target.value })} className="bg-fx-base border-white/10 font-mono" /></div>
          <div className="space-y-1"><Label className="text-xs">Max trade amount ($)</Label><Input data-testid="max-amount-input" type="number" value={cfg.max_trade_amount} onChange={(e) => upd({ max_trade_amount: e.target.value })} className="bg-fx-base border-white/10 font-mono" /></div>
          <div className="space-y-1"><Label className="text-xs">Default duration (sec)</Label><Input data-testid="default-duration-input" type="number" value={cfg.default_duration} onChange={(e) => upd({ default_duration: e.target.value })} className="bg-fx-base border-white/10 font-mono" /></div>
          <div className="space-y-1"><Label className="text-xs">Trading fee (%)</Label><Input data-testid="fee-input" type="number" value={cfg.trading_fee_pct} onChange={(e) => upd({ trading_fee_pct: e.target.value })} className="bg-fx-base border-white/10 font-mono" /></div>
        </div>
        <div className="space-y-1"><Label className="text-xs">Available durations (seconds, comma-separated)</Label><Input data-testid="durations-input" value={durInput} onChange={(e) => setDurInput(e.target.value)} placeholder="30, 60, 120, 300" className="bg-fx-base border-white/10 font-mono" /></div>
      </div>

      {/* Payouts & fees (business model controls for LIVE trades) */}
      <div className="rounded-xl bg-fx-s1 border border-white/10 p-4 space-y-3" data-testid="payouts-section">
        <div className="text-sm font-semibold">Payouts &amp; Fees <span className="text-fx-s3 font-normal">(applied to LIVE trades)</span></div>
        <p className="text-[11px] text-fx-s3">Payout % is the profit paid on a winning trade (e.g. 85% means $100 → $185 back). Set a default and override per asset. The trading fee above is charged on every live trade stake.</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Default payout %</Label><Input data-testid="default-payout-input" type="number" value={cfg.default_payout_pct} onChange={(e) => upd({ default_payout_pct: e.target.value })} className="bg-fx-base border-white/10 font-mono" /></div>
        </div>
        {assets.length > 0 && (
          <div className="space-y-2">
            <Label className="text-xs">Per-asset payout % (leave blank to use default)</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {assets.map((a) => (
                <div key={a.symbol} className="flex items-center gap-2 rounded-lg bg-fx-base border border-white/10 px-2 py-1.5">
                  <span className="text-xs font-mono text-fx-s3 flex-1 truncate">{a.symbol}</span>
                  <Input
                    data-testid={`payout-${a.symbol.replace('/', '-')}`}
                    type="number"
                    value={cfg.payouts?.[a.symbol] ?? ""}
                    onChange={(e) => setPayout(a.symbol, e.target.value)}
                    placeholder={String(cfg.default_payout_pct)}
                    className="h-7 w-16 bg-fx-s1 border-white/10 font-mono text-xs px-2"
                  />
                  <span className="text-xs text-fx-s3">%</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* External connection */}
      <div className="rounded-xl bg-fx-s1 border border-white/10 p-4 space-y-3" data-testid="external-connection">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold flex items-center gap-2"><Plug className="h-4 w-4 text-brandblue" /> External Exchange Connection</div>
          <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded flex items-center gap-1 ${cfg.external.status === "connected" ? "text-pos bg-pos/15" : "text-fx-s3 bg-white/5"}`}>
            {cfg.external.status === "connected" ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
            {cfg.external.status === "connected" ? "Connected" : "Not configured"}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Provider / Exchange</Label><Input data-testid="ext-provider-input" value={cfg.external.provider} onChange={(e) => updExt({ provider: e.target.value })} placeholder="e.g. Binance" className="bg-fx-base border-white/10" /></div>
          <label className="flex items-center gap-2 mt-6 cursor-pointer"><Switch data-testid="ext-sandbox-switch" checked={cfg.external.sandbox} onCheckedChange={(v) => updExt({ sandbox: v })} /><span className="text-sm">Sandbox / Test mode</span></label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">API Key {cfg.external.has_api_key && <span className="text-fx-s3">({cfg.external.api_key_masked})</span>}</Label><Input data-testid="ext-key-input" type="password" value={cfg.external._new_key || ""} onChange={(e) => updExt({ _new_key: e.target.value })} placeholder={cfg.external.has_api_key ? "•••• (unchanged)" : "Enter API key"} className="bg-fx-base border-white/10 font-mono text-xs" /></div>
          <div className="space-y-1"><Label className="text-xs">API Secret {cfg.external.has_api_secret && <span className="text-fx-s3">(stored)</span>}</Label><Input data-testid="ext-secret-input" type="password" value={cfg.external._new_secret || ""} onChange={(e) => updExt({ _new_secret: e.target.value })} placeholder={cfg.external.has_api_secret ? "•••• (unchanged)" : "Enter API secret"} className="bg-fx-base border-white/10 font-mono text-xs" /></div>
        </div>
        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <div className="text-sm">Enable external connection</div>
          <Switch data-testid="ext-enabled-switch" checked={cfg.external.enabled} onCheckedChange={(v) => updExt({ enabled: v })} />
        </label>
        <div className="flex items-center gap-2 text-[11px] text-fx-s3"><ShieldCheck className="h-3.5 w-3.5" /> Secrets are stored securely and never returned to the browser (only a masked hint is shown).</div>
        <Button variant="outline" data-testid="ext-test-btn" onClick={testConn} className="border-white/10 bg-fx-base">Test connection</Button>
      </div>

      <Button data-testid="trading-save-btn" disabled={busy} onClick={save} className="bg-brandblue hover:bg-blue-500">Save Trading Settings</Button>
    </div>
  );
}
